import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { RequestQueryBuilder, CondOperator } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { Observable, of, Subscription } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { Member } from 'src/app/features/member/member'
import { memberActions } from 'src/app/features/member/member.actions'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { CompetitionPlayer } from '../../competition-player/competition-player'
import { competitionPlayerActions } from '../../competition-player/competition-player.actions'
import { competitionPlayerSelectors } from '../../competition-player/competition-player.selectors'
import { CompetitionStaff } from '../../competition-staff/competition-staff'
import { competitionStaffActions } from '../../competition-staff/competition-staff.actions'
import { competitionStaffSelectors } from '../../competition-staff/competition-staff.selectors'
import { CompetitionTeamAddMemberModalComponent } from '../add-member-modal/show.component'
import { CompetitionTeam } from '../competition-team'
import { competitionTeamActions } from '../competition-team.actions'
import { competitionTeamSelectors } from '../competition-team.selectors'
import { competitionSportSelectors } from '../../competition-sport/competition-sport.selectors'
import { competitionSelectors } from '../../competition.selectors'
import { competitionActions } from '../../competition.actions'
import moment from 'moment-mini'
import { CompetitionStaffFunction } from '../../competition-staff-function/competition-staff-function'
import { competitionStaffFunctionSelectors } from '../../competition-staff-function/competition-staff-function.selectors'
import { ActivatedRoute } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
@UntilDestroy()
@Component({
  selector: 'competition-team-members-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionTeamMembersTabComponent implements OnInit, OnDestroy {
  @Output() onSubmit = new EventEmitter<any>()
  public teamId$: Observable<number>
  public team$: Observable<CompetitionTeam>
  public bsModalRef: BsModalRef
  public selectedMember: Member
  public members$: Observable<Member[]>
  public form: UntypedFormGroup
  public memberEntities$: Observable<Dictionary<Member>>
  public competitionStaff$: Observable<CompetitionStaff[]>
  public competitionSport$: Observable<any>
  public competitionPlayers$: Observable<CompetitionPlayer[]>
  private teamId: number
  public invitedPlayers: any[] = []
  public invitedStaffMembers: any[] = []
  public competitionStaffFunctionEntities$: Observable<
    Dictionary<CompetitionStaffFunction>
  >
  public invitedPlayers$: Observable<any[]>
  public invitedStaffMembers$: Observable<any[]>
  private subscriptions: Subscription[] = []

  constructor(
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private modalService: BsModalService,
    private fb: UntypedFormBuilder,
    private actions$: Actions,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.store
      .select(competitionSelectors.selectEntities)
      .pipe(take(1))
      .subscribe((competitions) => {
        // in this step must have competitions if not , possible page refresh , we update store
        if (Object.keys(competitions).length === 0) {
          this.store.dispatch(
            competitionActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  startDate: {
                    [CondOperator.GREATER_THAN]: moment().toISOString()
                  },
                  competitionLastRegistrationDate: {
                    [CondOperator.GREATER_THAN]: moment().toISOString()
                  }
                },
                join: [
                  { field: 'linkedChildProduct' },
                  { field: 'linkedChildProduct.translations' },
                  { field: 'competitionSportId' },
                  { field: 'competitionSport' }
                ]
              })
            })
          )
        }
      })
    this.form = this.fb.group({
      number: [null, [Validators.required]]
    })

    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )

    this.team$ = this.teamId$.pipe(
      tap((id) => {
        this.teamId = id
        this.store.dispatch(competitionTeamActions.fetchOneById({ id }))
      }),
      switchMap((teamId) =>
        this.store.select(competitionTeamSelectors.selectById, teamId)
      )
    )

    this.competitionSport$ = this.team$.pipe(
      untilDestroyed(this),
      switchMap((team) =>
        this.store.select(competitionSelectors.selectById, team?.competitionId)
      ),
      switchMap((competition) =>
        this.store.select(
          competitionSportSelectors.selectById,
          competition?.competitionSportId
        )
      )
    )

    this.team$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        filter((team) => Boolean(team))
      )
      .subscribe((team) => {
        this.store.dispatch(
          competitionTeamActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                $or: [
                  {
                    competitionId: team?.competitionId
                  },
                  {
                    id: team?.id
                  }
                ]
              }
            })
          })
        )
      })

    this.competitionStaff$ = this.store.select(
      competitionStaffSelectors.selectByFilter,
      {
        field: 'competitionTeamId',
        value: this.teamId
      }
    )

    this.competitionPlayers$ = this.store.select(
      competitionPlayerSelectors.selectByFilter,
      {
        field: 'competitionTeamId',
        value: this.teamId
      }
    )

    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)
    this.competitionStaffFunctionEntities$ = this.store.select(
      competitionStaffFunctionSelectors.selectEntities
    )

    this.invitedPlayers$ = this.store
      .select(competitionTeamSelectors.selectInvitedPlayers)
      .pipe(distinctUntilChanged())

    this.invitedStaffMembers$ = this.store
      .select(competitionTeamSelectors.selectInvitedStaffMembers)
      .pipe(distinctUntilChanged())
  }

  public onSearchMember(term: string) {
    this.store.dispatch(
      memberActions.searchManyByParams({
        // TODO should shift to another endpoint as /members is now for admins only
        params: RequestQueryBuilder.create({
          search: {
            $or: [
              {
                email: {
                  [CondOperator.EQUALS_LOW]: term
                }
              }
            ]
          }
        })
      })
    )

    this.members$ = this.store
      .select(memberSelectors.selectAll)
      .pipe(
        map((members) =>
          members.filter(
            (member) =>
              member?.email?.toLowerCase() == term.toLowerCase() ||
              member?.memberNumber == parseInt(term)
          )
        )
      )
  }

  public onSelectMember(member: Member) {
    this.selectedMember = member
  }

  public onClickAddStaff() {
    this.team$.pipe(take(1)).subscribe((team) => {
      this.bsModalRef = this.modalService.show(
        CompetitionTeamAddMemberModalComponent,
        {
          initialState: {
            role: 'staff',
            team
          }
        }
      )

      this.subscriptions.push(
        this.bsModalRef.content.invitedStaffMember
          .pipe(take(1), withLatestFrom(this.invitedStaffMembers$))
          .subscribe(([invitedStaffMember, invitedStaffMembers]) => {
            if (invitedStaffMember) {
              this.invitedStaffMembers = [
                ...invitedStaffMembers,
                invitedStaffMember
              ]

              this.store.dispatch(
                competitionTeamActions.setInvitedStaffMembers({
                  entity: this.invitedStaffMembers
                })
              )
            }
          })
      )
    })
  }

  public onClickAddPlayer() {
    this.team$.pipe(take(1)).subscribe((team) => {
      this.bsModalRef = this.modalService.show(
        CompetitionTeamAddMemberModalComponent,
        {
          initialState: {
            role: 'player',
            team
          }
        }
      )

      this.subscriptions.push(
        this.bsModalRef.content.invitedPlayer
          .pipe(take(1), withLatestFrom(this.invitedPlayers$))
          .subscribe(([invitedPlayer, invitedPlayers]) => {
            if (invitedPlayer) {
              this.invitedPlayers = [...invitedPlayers, invitedPlayer]

              this.store.dispatch(
                competitionTeamActions.setInvitedPlayers({
                  entity: this.invitedPlayers
                })
              )
            }
          })
      )
    })
  }

  public onClickRemoveStaff(staffId: number) {
    const message = this.translate.instant(
      'competitionModule.removeStaffMember'
    )
    if (!window.confirm(message)) {
      return
    }

    this.store.dispatch(competitionStaffActions.deleteOne({ id: staffId }))

    this.actions$
      .pipe(
        ofType(competitionStaffActions.removeOne),
        first(),
        timeout(5000),
        catchError((error) => {
          return of(null)
        })
      )
      .subscribe((action) => {
        if (!action) {
          this.notificationService.emit('alerts.timeoutError', 'warning')
          return
        }
        this.notificationService.emit('alerts.success', 'success')
      })
  }

  public onClickRemovePlayer(staffId: number) {
    const message = this.translate.instant('competitionModule.removePlayer')
    if (!window.confirm(message)) {
      return
    }

    this.store.dispatch(competitionPlayerActions.deleteOne({ id: staffId }))

    this.actions$
      .pipe(
        ofType(competitionPlayerActions.removeOne),
        first(),
        timeout(5000),
        catchError((error) => {
          return of(null)
        })
      )
      .subscribe((action) => {
        if (!action) {
          this.notificationService.emit('alerts.timeoutError', 'warning')
          return
        }
        this.notificationService.emit('alerts.success', 'success')
      })
  }

  public onClickRemoveInvitedMember(email: string, role: string) {
    const message = this.translate.instant('competitionModule.removeMember', {
      role: this.translate.instant(`competitionModule.${role}`)
    })
    if (!window.confirm(message)) {
      return
    }

    switch (role) {
      case 'player':
        this.store
          .select(competitionTeamSelectors.selectInvitedPlayers)
          .pipe(take(1))
          .subscribe((invitedPlayers) => {
            const filteredInvitedPlayers = invitedPlayers.filter(
              (invitedPlayer) =>
                invitedPlayer.email != email &&
                invitedPlayer.teamId == this.teamId
            )

            this.store.dispatch(
              competitionTeamActions.setInvitedPlayers({
                entity: filteredInvitedPlayers
              })
            )
          })
        break
      case 'staff':
        this.store
          .select(competitionTeamSelectors.selectInvitedStaffMembers)
          .pipe(take(1))
          .subscribe((invitedStaffMembers) => {
            const filteredInvitedStaffMembers = invitedStaffMembers.filter(
              (invitedStaffMember) =>
                invitedStaffMember.email != email &&
                invitedStaffMember.teamId == this.teamId
            )

            this.store.dispatch(
              competitionTeamActions.setInvitedStaffMembers({
                entity: filteredInvitedStaffMembers
              })
            )
          })
    }
  }

  ngOnDestroy() {
    this.subscriptions.map((s) => s.unsubscribe())
  }
}
